import { CARD_WIDTH } from "../types/Card";
import { AnimationEffects } from "../types/Animation";
import { TRANSFORM_STYLES } from "../types/Discard";

let cachedDeckRect: DOMRect | null = null;
let cachedDeckFrame = -1;

export const getDeckRect = (): DOMRect | null => {
    const frame = performance.now();

    // Cache for the duration of one frame (~16ms)
    if(cachedDeckRect && frame - cachedDeckFrame < 16) {
        return cachedDeckRect;
    }

    const deck = document.querySelector("#deck");
    const deckCard = deck?.querySelector(".card-container") || deck;
    cachedDeckRect = deckCard?.getBoundingClientRect() ?? null;
    cachedDeckFrame = frame;

    return cachedDeckRect;
};

const isCenteredScale = (el: HTMLElement, matrix: DOMMatrixReadOnly): boolean => {
    if(matrix.b !== 0 || matrix.c !== 0) {
        return false;
    }

    const [originX, originY] = getComputedStyle(el).transformOrigin.split(" ").map(parseFloat);

    return Math.abs(originX - el.offsetWidth / 2) < 0.5 && Math.abs(originY - el.offsetHeight / 2) < 0.5;
};

export const measureLayoutRect = (el: HTMLElement): { layoutRect: DOMRect, fanTransform: string } => {
    const fanTransform = getComputedStyle(el).transform;
    const parent = el.parentElement;

    if(parent?.dataset.cardSlot !== undefined) {
        return { layoutRect: parent.getBoundingClientRect(), fanTransform };
    }

    const matrix = fanTransform === "none" ? new DOMMatrixReadOnly() : new DOMMatrixReadOnly(fanTransform);

    if(isCenteredScale(el, matrix)) {
        const rect = el.getBoundingClientRect();
        const width = rect.width / matrix.a;
        const height = rect.height / matrix.d;

        const centerX = rect.left + rect.width / 2 - matrix.e;
        const centerY = rect.top + rect.height / 2 - matrix.f;

        return {
            layoutRect: new DOMRect(centerX - width / 2, centerY - height / 2, width, height),
            fanTransform,
        };
    }

    const savedTransform = el.style.transform;
    const savedOrigin = el.style.transformOrigin;
    el.style.transform = "";
    el.style.transformOrigin = "";
    const layoutRect = el.getBoundingClientRect();
    el.style.transform = savedTransform;
    el.style.transformOrigin = savedOrigin;
    return { layoutRect, fanTransform };
};

export const computeDeckToHandEffects = (
    el: HTMLElement,
    deckRotation: number,
    targetScale: number
): AnimationEffects[] | null => {
    const deckRect = getDeckRect();

    if(!deckRect) {
        return null;
    }

    const { layoutRect, fanTransform } = measureLayoutRect(el);

    const visualWidth = deckRotation === 0 ? deckRect.width : deckRect.height;
    const deckScale = visualWidth / CARD_WIDTH;

    const deckX = (deckRect.left + deckRect.width / 2) - layoutRect.left - layoutRect.width / 2;
    const deckY = (deckRect.top + deckRect.height / 2) - layoutRect.top - layoutRect.height / 2;

    const endTransform = fanTransform !== "none"
        ? fanTransform
        : `translate(0px, 0px) scale(${targetScale}) rotate(0deg)`;

    return [{
        transform: [
            `translate(${deckX}px, ${deckY}px) scale(${deckScale}) rotate(${deckRotation}deg)`,
            endTransform
        ],
    }];
};

export const getDiscardRotation = (positionInDiscardStack: number): number => {
    const styleIndex = positionInDiscardStack % Object.keys(TRANSFORM_STYLES).length;
    return TRANSFORM_STYLES[styleIndex as keyof typeof TRANSFORM_STYLES].rotation;
};
